import * as React from 'react'
import { observer, inject } from 'mobx-react'
import { API, IPricingGroup, IBaseProduct } from '@getgreenline/homi-shared'
import { CreateProduct, CreateChildProduct } from '../ProductStore'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { EditVariant } from './EditVariant'
import { TemplateSection } from '../TemplateSection'
import { message, Collapse, Icon, Tooltip } from 'antd'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import { LocalStorage } from '../../../../../utilities/LocalStorage'
import { ProvinceProductModels } from '@getgreenline/products'
import { IGlobalProduct } from '@getgreenline/global-products'

interface Props {
  product: CreateProduct
  pricingGroups: IPricingGroup[]
  companyId: number
  currentCompanyStore?: CurrentCompanyStore
  activeKeys: string[]
  allActiveKeys: string[]
  setActiveKeys: (activeKeys: string[]) => void
  searchDuplicateSkus: (inputSku: string, productResults: IBaseProduct[]) => void
  populateProduct: (
    products: ProvinceProductModels.IProvinceProductContract[],
    supplierId?: number,
  ) => void
  populateGlobalProduct: (products: IGlobalProduct[], supplierId?: number) => void
  blockSaving: (blockSaving: boolean, errorMessage?: string) => void
  loadProduct: (productId: string) => void
  setVariantsSectionRef: (el: HTMLDivElement) => void
  autoScrollToVariants: () => void
}

interface State {
  showCopyPopover: boolean
  tagsToBeCreated: { [tagName: string]: number }
}

@inject('currentCompanyStore')
@observer
export class VariantsSection extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      showCopyPopover: false,
      tagsToBeCreated: {},
    }
  }

  deleteChildProduct = async (childProduct: CreateChildProduct, index: number) => {
    try {
      if (childProduct.id) {
        await API.deleteProduct(this.props.currentCompanyStore!.company!.id, childProduct.id)
      }
      this.props.product.removeChildProduct(index)
      const activeKeys = this.props.activeKeys
      const indexToRemove = activeKeys.indexOf(index.toString())
      if (indexToRemove !== -1) {
        activeKeys.splice(indexToRemove, 1)
      }
      const updatedActiveKeys = activeKeys.map((key) =>
        Number(key) > index ? (Number(key) - 1).toString() : key,
      )
      this.props.setActiveKeys(updatedActiveKeys)
      message.success(`Successfully deleted ${childProduct.name}`)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'Error deleting product')
    }
  }

  get collapseState(): 'Expand all' | 'Collapse all' {
    return this.props.activeKeys.length === 0 ? 'Expand all' : 'Collapse all'
  }

  render() {
    const { product, companyId, searchDuplicateSkus, pricingGroups, activeKeys, setActiveKeys } =
      this.props
    const childProducts = product.childProducts
    let matchingPricingGroup: IPricingGroup | null = null
    if (product.pricingGroupId) {
      matchingPricingGroup = pricingGroups.find((pg) => pg.id === product.pricingGroupId) || null
    }
    const pricingGroupVariants = matchingPricingGroup ? matchingPricingGroup.childProducts : []

    if (product.needsPricingGroupRefresh) {
      return (
        <div className='card mb-4'>
          <div className='card-body'>
            <div className='col-12'>
              <h3 className='text-muted'>Pricing group variants update preview</h3>
              {childProducts.filter((cp) => cp.id).length > 0 && (
                <div>
                  {childProducts.map((childProduct, index) => {
                    const matchingChildPricingGroup = pricingGroupVariants.find(
                      (pgv) => pgv.id === childProduct.pricingGroupId,
                    )

                    return (
                      <div className='row mb-2' key={index}>
                        <div className='col-lg-6'>
                          <div>Existing variant: {childProduct.name}</div>
                          <small className='text-muted'>ID: {childProduct.id}</small>
                        </div>
                        <div className='col-lg-6'>
                          <div className={matchingChildPricingGroup ? '' : 'text-danger'}>
                            Pricing group:
                            {matchingChildPricingGroup
                              ? matchingChildPricingGroup.name
                              : 'Not selected. This variant will be removed'}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )
    }

    const updateCreatedTagsContext = (tagName: string, addition: number, callback?: () => void) => {
      /*
      state.tagsToBeCreated represents an object which takes a tag-name as a key, and a numeric value which describes the amount of variant-products that the tag is currently assigned to
      This function is used to increment/decrement that count for a given tag.

      State.tagsToBeCreated and updateCreatedTagsContext work to make it so that when a tag is set to be created within the company on one variant, the other variants will have that tag visible in their tag drop-downs.
      When the count for a tag reaches 0, that means that no variant is currently selecting that tag, which means that it no longer needs to be created within the company, so it is removed from the tag list in all variants.
      */

      const newCount = (this.state.tagsToBeCreated?.[tagName] ?? 0) + addition
      const newTagsToBeCreated = {
        ...this.state.tagsToBeCreated,
        [tagName]: newCount > 0 ? newCount : 0,
      }

      this.setState(
        {
          tagsToBeCreated: newTagsToBeCreated,
        },
        callback,
      )
    }

    return (
      <div className='card add-product-variants-section mb-4'>
        <div className='card-header d-flex'>
          <div style={{ flexGrow: 1 }}>Variants</div>
          <a
            onClick={() => {
              LocalStorage.setOpenVariantsByDefault(this.collapseState === 'Expand all')
              const activeKeys = this.collapseState === 'Expand all' ? this.props.allActiveKeys : []
              setActiveKeys(activeKeys)
            }}
          >
            {this.collapseState}
          </a>
        </div>
        <div className='card-body auto-scroll-margin' ref={this.props.setVariantsSectionRef}>
          {(product.isMasterProduct || childProducts.length > 0) && (
            <Collapse
              defaultActiveKey={this.props.activeKeys}
              expandIconPosition='left'
              activeKey={this.props.activeKeys}
              onChange={(keys) => setActiveKeys([...keys])}
              expandIcon={({ isActive }) => (
                <Tooltip title={isActive ? 'Collapse' : 'Expand'}>
                  <Icon
                    type='right-circle'
                    theme='twoTone'
                    style={{ fontSize: 18, marginLeft: -5 }}
                    rotate={isActive ? 90 : 0}
                  />
                </Tooltip>
              )}
            >
              {childProducts.map((childProduct, index) => (
                <EditVariant
                  updateCreatedTagsContext={updateCreatedTagsContext}
                  tagsToBeCreated={this.state.tagsToBeCreated}
                  key={index}
                  index={index}
                  parentProduct={product}
                  childProduct={childProduct}
                  currentCompanyStore={this.props.currentCompanyStore}
                  searchDuplicateSkus={searchDuplicateSkus}
                  onDelete={() => {
                    this.deleteChildProduct(childProduct, index)
                  }}
                  onRemoveChildProduct={() => this.props.product.removeChildProduct(index)}
                  blockSaving={this.props.blockSaving}
                  loadProduct={this.props.loadProduct}
                />
              ))}
            </Collapse>
          )}

          {!product.id && product.pricingGroupId && (
            <div className='mb-2 text-muted'>
              <i className='mr-2 fa fa-exclamation-triangle text-warning' />
              <span>
                Saving the product will populate variants from your selected pricing group.
              </span>
              <a target='__blank' href={`/company/${companyId}/dashboard/products/pricingGroups`}>
                View pricing groups
              </a>
            </div>
          )}

          <div className='row' style={{ marginTop: 15, marginBottom: -15 }}>
            <BlazeButton
              buttonProps={{
                icon: 'plus',
                style: {
                  marginLeft: 15,
                  marginRight: 12,
                  marginBottom: 15,
                  backgroundColor: 'var(--light)',
                },
                onClick: () => {
                  product.addChildProduct()
                  setActiveKeys([...activeKeys, (product.childProducts.length - 1).toString()])
                },
                disabled: !!product.pricingGroupId || product.isDeleted,
              }}
              titleProps={{
                title: 'Add variant',
              }}
            />

            {this.props.product.isMasterProduct && !product.isDeleted && (
              <TemplateSection
                populateProduct={this.props.populateProduct}
                populateGlobalProduct={this.props.populateGlobalProduct}
                product={this.props.product}
                currentCompanyStore={this.props.currentCompanyStore}
                activeKeys={activeKeys}
                setActiveKeys={setActiveKeys}
                autoScrollToVariants={this.props.autoScrollToVariants}
              />
            )}
          </div>
        </div>
      </div>
    )
  }
}
